/**
 * ItineraryPreview - Premium sample-style preview matching the reference PDF.
 * Features: stacked "Day" labels in EB Garamond italic + huge bold numbers,
 * organic notched image cards (using SVG masks), dotted connecting lines,
 * image collage cover, premium typography.
 */

const TEAL = "#054A4D";
const TEAL_DARK = "#054A4D";
const INK = "#0A1220";
const MUTED = "#374151";
const MINT = "#EAF7F6";
const LINE = "#A8DAD6";
const GLASS_BG = "rgba(255,255,255,0.65)";
const GLASS_BORDER = "rgba(168,218,214,0.6)";
const TEAL_LIGHT = "rgba(5,74,77,0.06)";

function NotchedImage({ src, className = "", style = {}, variant = "default" }) {
  // Different border radius variants to create organic shape feel
  const variants = {
    default: { borderRadius: "16px 16px 16px 60px" },
    left: { borderRadius: "60px 16px 16px 16px" },
    right: { borderRadius: "16px 60px 16px 16px" },
    bottom: { borderRadius: "16px 16px 60px 16px" },
    pill: { borderRadius: "120px 16px 16px 120px" },
    pillRight: { borderRadius: "16px 120px 120px 16px" },
  };
  return (
    <img
      src={src}
      alt=""
      className={`object-cover ${className}`}
      style={{ ...variants[variant], ...style }}
    />
  );
}

function DotLine({ vertical = false, className = "" }) {
  return (
    <div
      className={className}
      style={{
        backgroundImage: `radial-gradient(circle, ${TEAL} 1.5px, transparent 1.5px)`,
        backgroundSize: vertical ? "5px 8px" : "8px 5px",
        backgroundRepeat: vertical ? "repeat-y" : "repeat-x",
        width: vertical ? "2px" : "100%",
        height: vertical ? "100%" : "2px",
      }}
    />
  );
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline-block", marginLeft: 6, verticalAlign: "middle" }}>
      <path d="M7 17L17 7M17 7H9M17 7V15" />
    </svg>
  );
}

function DayBadge({ num }) {
  // HORIZONTAL compact "Day [number]" - matches sample, no vertical waste
  return (
    <span style={{ display: "inline-block", lineHeight: 1, whiteSpace: "nowrap" }}>
      <span style={{ fontFamily: "'EB Garamond', Georgia, serif", fontStyle: "italic", fontWeight: 500, color: TEAL, fontSize: 30, verticalAlign: "baseline" }}>Day</span>
      <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 800, color: TEAL, fontSize: 48, letterSpacing: "-1.5px", verticalAlign: "baseline", marginLeft: 6 }}>{num}</span>
    </span>
  );
}

export default function ItineraryPreview({ record }) {
  if (!record) return null;
  const data = record.itinerary_data || {};
  const days = data.days || [];
  const title = data.trip_title || record.destination;
  const route = data.route_summary || `${record.starting_point || "India"} — ${record.destination}`;
  const totalDays = record.days || data.total_days || days.length;
  const totalNights = record.nights || data.total_nights || Math.max(totalDays - 1, 0);
  const travelMonth = record.travel_month || data.travel_month || "";
  const highlights = data.highlights || [];
  const inclusions = record.inclusions || data.inclusions || [];
  const exclusions = record.exclusions || data.exclusions || [];
  const tc = record.terms_conditions || "";
  const hotelInfo = record.hotel_info || {};
  const packages = record.packages || [];

  // Collect cover collage images (first 4-5 from days)
  const collageImgs = [];
  for (const d of days) {
    for (const im of (d.images || [])) {
      const url = im.url || im.thumb;
      if (url && !collageImgs.includes(url)) collageImgs.push(url);
      if (collageImgs.length >= 5) break;
    }
    if (collageImgs.length >= 5) break;
  }

  return (
    <div className="pdf-root" style={{
      fontFamily: "'Inter', 'Helvetica', Arial, sans-serif",
      color: INK,
      background: `radial-gradient(ellipse at 15% 15%, rgba(255,192,203,0.35) 0%, transparent 50%),
                   radial-gradient(ellipse at 85% 85%, rgba(173,216,230,0.35) 0%, transparent 50%),
                   radial-gradient(ellipse at 55% 35%, rgba(220,215,255,0.25) 0%, transparent 45%),
                   #f8fafc`,
      minHeight: "100vh"
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500;1,600;1,700&family=Playfair+Display:wght@600;700;800;900&family=Inter:wght@400;500;600;700&display=swap');
      `}</style>

      {/* COVER */}
      <section style={{ padding: "36px 32px 40px", position: "relative", borderBottom: `1px solid ${LINE}`, background: "rgba(255,255,255,0.60)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)", borderRadius: "0 0 24px 24px", boxShadow: "0 4px 32px rgba(5,74,77,0.07)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
          <div>
            <div style={{ color: TEAL, fontWeight: 800, letterSpacing: 3, fontSize: 13, fontFamily: "'Inter', sans-serif" }}>HAPPY JOURNEY 4 U</div>
            <div style={{ color: MUTED, fontSize: 13, marginTop: 2, fontStyle: "italic", fontFamily: "'EB Garamond', serif" }}>by Lucky Jeswani</div>
          </div>
          <div style={{ background: TEAL, color: "white", padding: "9px 20px", borderRadius: 999, fontSize: 13, fontWeight: 700, letterSpacing: 1 }}>
            {totalDays} DAYS / {totalNights} NIGHTS
          </div>
        </div>

        <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 46, fontWeight: 800, color: TEAL, lineHeight: 1.05, margin: "10px 0 8px", letterSpacing: "-0.5px" }}>
          {title}
        </h1>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <DotLine className="" />
        </div>
        <p style={{ color: TEAL_DARK, fontSize: 18, fontStyle: "italic", fontFamily: "'EB Garamond', serif", margin: "8px 0" }}>{route}</p>
        {travelMonth && (
          <p style={{ color: "#6b7280", fontSize: 14, margin: "2px 0 14px", letterSpacing: 0.5 }}>
            <span style={{ fontWeight: 600 }}>TRAVEL DATE:</span> {travelMonth}
          </p>
        )}

        {/* Organic image collage - matches sample's overlapping cards */}
        {collageImgs.length > 0 && (
          <div style={{ position: "relative", marginTop: 18, marginBottom: 8 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gridTemplateRows: "120px 120px", gap: 10 }}>
              {collageImgs[0] && (
                <NotchedImage src={collageImgs[0]} variant="left" style={{ width: "100%", height: "100%", gridRow: "1 / span 2" }} />
              )}
              {collageImgs[1] && <NotchedImage src={collageImgs[1]} variant="right" style={{ width: "100%", height: "100%" }} />}
              {collageImgs[2] && <NotchedImage src={collageImgs[2]} variant="bottom" style={{ width: "100%", height: "100%" }} />}
              {collageImgs[3] && <NotchedImage src={collageImgs[3]} variant="default" style={{ width: "100%", height: "100%" }} />}
              {collageImgs[4] && <NotchedImage src={collageImgs[4]} variant="pillRight" style={{ width: "100%", height: "100%" }} />}
            </div>
          </div>
        )}

        {data.overview && (
          <p style={{ marginTop: 18, fontSize: 15, lineHeight: 1.8, color: "#374151", fontFamily: "'EB Garamond', serif" }}>
            {data.overview}
          </p>
        )}

        {/* Meals/Accommodation pill row */}
        <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
          <div style={{ background: MINT, border: `1px solid ${LINE}`, borderRadius: 999, padding: "6px 14px", fontSize: 11 }}>
            <span style={{ color: TEAL, fontWeight: 700, letterSpacing: 1 }}>MEALS: </span>
            <span style={{ color: INK }}>Breakfast & Dinner</span>
          </div>
          <div style={{ background: MINT, border: `1px solid ${LINE}`, borderRadius: 999, padding: "6px 14px", fontSize: 11 }}>
            <span style={{ color: TEAL, fontWeight: 700, letterSpacing: 1 }}>STAY: </span>
            <span style={{ color: INK }}>{hotelInfo.category || "Premium Hotels & Resorts"}</span>
          </div>
        </div>

        {/* Prepared For card - glassmorphism */}
        <div style={{ marginTop: 18, background: GLASS_BG, backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: `1.5px solid ${GLASS_BORDER}`, borderRadius: 20, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, boxShadow: "0 4px 24px rgba(5,74,77,0.08)" }}>
          <div>
            <div style={{ color: TEAL, fontSize: 11, fontWeight: 700, letterSpacing: 1.5 }}>PREPARED FOR</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: INK, marginTop: 2 }}>{record.customer_name}</div>
            <div style={{ fontSize: 13, color: MUTED, marginTop: 1 }}>
              {[record.customer_contact, record.customer_email].filter(Boolean).join(" · ")}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ color: TEAL, fontSize: 11, fontWeight: 700, letterSpacing: 1.5 }}>TRAVELERS</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: INK, marginTop: 2 }}>
              {record.travelers_adults} Adult{record.travelers_adults > 1 ? "s" : ""}
              {record.travelers_children ? `, ${record.travelers_children} Child${record.travelers_children > 1 ? "ren" : ""}` : ""}
              {record.travelers_infants ? `, ${record.travelers_infants} Infant${record.travelers_infants > 1 ? "s" : ""}` : ""}
            </div>
          </div>
        </div>

        {highlights.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: TEAL, fontSize: 17, whiteSpace: "nowrap" }}>✦ Trip Highlights</span>
              <div style={{ flex: 1, height: 1, background: LINE }} />
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {highlights.map((h, i) => (
                <div key={i} style={{
                  background: GLASS_BG,
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  border: `1px solid ${GLASS_BORDER}`,
                  borderRadius: 999,
                  padding: "5px 14px",
                  fontSize: 13,
                  color: TEAL,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  boxShadow: "0 1px 6px rgba(5,74,77,0.07)"
                }}>
                  <span style={{ fontSize: 13 }}>⭐</span>
                  <span>{h}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* DAY-BY-DAY */}
      <section style={{ padding: "28px 32px 12px", background: "rgba(255,255,255,0.45)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", margin: "16px 0", borderTop: `1px solid ${GLASS_BORDER}`, borderBottom: `1px solid ${GLASS_BORDER}` }}>
        {/* Per-page branding strip */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, paddingBottom: 10, borderBottom: `1px solid ${LINE}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 22, height: 22, borderRadius: 6, background: TEAL, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: "white", fontSize: 12 }}>✈</span>
            </div>
            <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: TEAL, fontSize: 13 }}>Happy Journey 4 U</span>
            <span style={{ color: LINE, fontSize: 12 }}>·</span>
            <span style={{ fontFamily: "'EB Garamond', serif", fontStyle: "italic", color: MUTED, fontSize: 11 }}>by Lucky Jeswani</span>
          </div>
          <span style={{ fontSize: 10, color: MUTED, letterSpacing: 0.5 }}>{record.customer_name} · {totalDays}D/{totalNights}N</span>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 20 }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 28, color: TEAL, margin: 0 }}>Day-by-Day Itinerary</h2>
          <div style={{ flex: 1, height: 1, background: LINE }} />
        </div>

        {days.map((d, i) => {
          const imgs = (d.images || []).slice(0, 3).map(im => im.url || im.thumb).filter(Boolean);
          const variants = ["left", "right", "bottom", "default", "pill"];
          const isEven = i % 2 === 0;
          return (
            <div key={i} style={{ position: "relative", marginBottom: 28, paddingBottom: 8 }}>
              {/* Connecting dotted line between days */}
              {i < days.length - 1 && (
                <div style={{ position: "absolute", left: 24, bottom: -22, width: 2, height: 28, backgroundImage: `radial-gradient(circle, ${TEAL} 1.5px, transparent 1.5px)`, backgroundSize: "2px 8px", backgroundRepeat: "repeat-y" }} />
              )}
              <div style={{
                display: "grid",
                gridTemplateColumns: isEven ? "minmax(0,1fr) minmax(0,1.15fr)" : "minmax(0,1.15fr) minmax(0,1fr)",
                gap: 16,
                alignItems: "stretch"
              }}>
                {/* IMAGES SIDE — top img 50%, bottom row 50% */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8, order: isEven ? 0 : 2, overflow: "hidden", minWidth: 0 }}>
                  {/* Top image — 50% of total height */}
                  {imgs[0] && (
                    <div style={{ flex: "1 1 0", minHeight: 0, overflow: "hidden" }}>
                      <NotchedImage
                        src={imgs[0]}
                        variant={isEven ? "left" : "right"}
                        style={{ width: "100%", height: "100%", minHeight: 130, display: "block" }}
                      />
                    </div>
                  )}
                  {/* Bottom row — 50% of total height, split between 2 images */}
                  {imgs[1] && (
                    <div style={{ display: "flex", gap: 8, flex: "1 1 0", minHeight: 0, overflow: "hidden" }}>
                      <div style={{ flex: "1 1 0", minWidth: 0, overflow: "hidden" }}>
                        <NotchedImage src={imgs[1]} variant={isEven ? "bottom" : "default"} style={{ width: "100%", height: "100%", minHeight: 90, display: "block" }} />
                      </div>
                      {imgs[2] && (
                        <div style={{ flex: "1 1 0", minWidth: 0, overflow: "hidden" }}>
                          <NotchedImage src={imgs[2]} variant="default" style={{ width: "100%", height: "100%", minHeight: 90, display: "block" }} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* TEXT SIDE */}
                <div style={{ order: 1, position: "relative", minWidth: 0, background: GLASS_BG, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", borderRadius: 16, padding: "14px 16px", border: `1px solid ${GLASS_BORDER}`, boxShadow: "0 2px 16px rgba(5,74,77,0.06)" }}>
                  <div style={{ display: "flex", justifyContent: isEven ? "flex-end" : "flex-start", marginBottom: 4 }}>
                    <DayBadge num={d.day_number} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: -8 }}>
                    <span style={{ color: TEAL, fontWeight: 700, fontSize: 10, letterSpacing: 2 }}>DETAILED ITINERARY:</span>
                    <ArrowIcon />
                  </div>
                  <DotLine className="" />
                  <h4 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: TEAL, margin: "10px 0 4px", fontSize: 19 }}>{d.route || d.title}</h4>
                  {/* Stats: Distance + Drive — bolded with icons */}
                  <div style={{ display: "flex", gap: 8, margin: "6px 0 6px", flexWrap: "wrap" }}>
                    {d.distance && (
                      <div style={{ background: MINT, border: `1px solid ${LINE}`, borderRadius: 999, padding: "4px 12px", fontSize: 11, fontWeight: 700, color: TEAL, display: "flex", alignItems: "center", gap: 4 }}>
                        <span>🚗</span><span>{d.distance}</span>
                      </div>
                    )}
                    {d.drive_time && (
                      <div style={{ background: MINT, border: `1px solid ${LINE}`, borderRadius: 999, padding: "4px 12px", fontSize: 11, fontWeight: 700, color: TEAL, display: "flex", alignItems: "center", gap: 4 }}>
                        <span>⏱</span><span>{d.drive_time}</span>
                      </div>
                    )}
                  </div>
                  <DotLine />
                  {/* Description: first 3 lines as crisp summary */}
                  {d.description && (
                    <p style={{ fontSize: 15, lineHeight: 1.75, color: "#374151", marginTop: 8, fontFamily: "'EB Garamond', serif" }}>
                      {d.description}
                    </p>
                  )}
                  {d.activities?.length > 0 && (
                    <div style={{
                      margin: "10px 0 6px",
                      background: "rgba(255,255,255,0.55)",
                      backdropFilter: "blur(12px)",
                      WebkitBackdropFilter: "blur(12px)",
                      border: `1px solid rgba(168,218,214,0.55)`,
                      borderRadius: 14,
                      padding: "10px 14px",
                      boxShadow: "0 2px 12px rgba(5,74,77,0.07), inset 0 1px 0 rgba(255,255,255,0.8)"
                    }}>
                      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                        {d.activities.slice(0, 4).map((a, ai) => (
                          <li key={ai} style={{ marginBottom: 10, display: "flex", gap: 10, alignItems: "flex-start" }}>
                            <div style={{ marginTop: 4, flexShrink: 0, width: 10, height: 10, borderRadius: "50%", background: TEAL, border: `2px solid ${LINE}`, boxShadow: `0 0 0 3px rgba(234,247,246,0.8)` }} />
                            <div style={{ fontFamily: "'EB Garamond', serif", fontWeight: 600, fontStyle: "italic", fontSize: 14.5, color: INK, lineHeight: 1.45 }}>{a}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                    {d.meals && (
                      <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: MUTED }}>
                        <span>🍽</span><span style={{ color: TEAL, fontWeight: 700 }}>Meals:</span><span>{d.meals}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* ============================================
          HOTEL, PACKAGES, INC/EXC, T&C
          ============================================ */}
      {(hotelInfo.name || hotelInfo.images?.length) && (
        <section style={{ padding: "16px 28px" }}>
          <div style={{ background: MINT, borderRadius: 20, padding: "20px 22px", border: `1px solid ${LINE}` }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", color: TEAL, fontSize: 22, fontWeight: 700, margin: "0 0 6px" }}>Accommodation</h2>
            <p style={{ margin: "2px 0", fontFamily: "'EB Garamond', serif", fontSize: 14 }}><b>Hotel:</b> {hotelInfo.name || "—"}</p>
            <p style={{ margin: "2px 0", fontFamily: "'EB Garamond', serif", fontSize: 14 }}><b>Category:</b> {hotelInfo.category || "—"}</p>
            {hotelInfo.notes && <p style={{ margin: "8px 0", fontSize: 12, color: MUTED, fontStyle: "italic" }}>{hotelInfo.notes}</p>}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginTop: 12 }}>
              {(hotelInfo.images || []).map((im, i) => (
                <img key={i} src={im.url} alt="" style={{ width: "100%", height: 160, objectFit: "cover", borderRadius: 12, border: `1px solid ${LINE}`, boxShadow: "0 4px 12px rgba(5,74,77,0.08)" }} />
              ))}
            </div>
          </div>
        </section>
      )}

      {packages.length > 0 && packages.some((p, i) => days.some(d => d.package_hotels && d.package_hotels[i] && (d.package_hotels[i].hotel_name || d.package_hotels[i].hotel_images?.length > 0))) && (
        <section style={{ padding: "0 28px 16px" }}>
          <div style={{ background: "rgba(255,255,255,0.6)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", borderRadius: 20, padding: "20px 22px", border: `1px solid ${GLASS_BORDER}`, boxShadow: "0 4px 16px rgba(5,74,77,0.05)" }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", color: TEAL, fontSize: 22, fontWeight: 700, margin: "0 0 16px" }}>Accommodation Options</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              {packages.map((p, i) => {
                const pkgDays = days.filter(d => d.package_hotels && d.package_hotels[i] && (d.package_hotels[i].hotel_name || d.package_hotels[i].hotel_images?.length > 0));
                
                const uniqueHotels = [];
                const seenKeys = new Set();
                pkgDays.forEach(d => {
                  const ph = d.package_hotels[i];
                  let city = (d.accommodation || "").trim();
                  if (city.toLowerCase().includes(" in ")) {
                    city = city.split(/ in /i).pop().trim();
                  }
                  const key = `${city}-${ph.hotel_name}`;
                  if (!seenKeys.has(key)) {
                    seenKeys.add(key);
                    uniqueHotels.push({ ph, city: city || `Day ${d.day_number}` });
                  }
                });

                if (uniqueHotels.length === 0) return null;
                
                return (
                  <div key={i} style={{ borderBottom: i < packages.length - 1 ? `1px dashed ${LINE}` : "none", paddingBottom: i < packages.length - 1 ? 24 : 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                      <div style={{ background: TEAL, color: "white", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>{p.name}</div>
                    </div>
                    
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                      {uniqueHotels.map(({ ph, city }, dIdx) => {
                        return (
                          <div key={dIdx} style={{ background: "rgba(255,255,255,0.4)", borderRadius: 12, padding: "12px 14px", border: `1px solid rgba(168,218,214,0.3)` }}>
                             <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
                               <span style={{ fontSize: 12, fontWeight: 700, color: TEAL }}>📍 {city}</span>
                               <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, color: INK, fontWeight: 700 }}>{ph.hotel_name || "—"}</span>
                               {ph.hotel_category && <span style={{ fontSize: 12, color: MUTED }}>({ph.hotel_category})</span>}
                             </div>
                             {ph.hotel_notes && <p style={{ margin: "0 0 8px", fontSize: 13, color: MUTED, fontStyle: "italic" }}>{ph.hotel_notes}</p>}
                             <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, width: "100%" }}>
                               {(ph.hotel_images || []).map((im, idx) => (
                                 <img key={idx} src={im.url} alt="" style={{ width: "100%", height: 140, objectFit: "cover", borderRadius: 12, border: `1px solid ${GLASS_BORDER}`, boxShadow: "0 2px 8px rgba(5,74,77,0.06)" }} />
                               ))}
                             </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {packages.length > 0 && (
        <section style={{ padding: "12px 28px" }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: TEAL, fontSize: 22, fontWeight: 700, margin: "8px 0 12px", display: "flex", alignItems: "baseline", gap: 12 }}>
            Package Options
            <div style={{ flex: 1, height: 1, background: LINE }} />
          </h2>
          <div style={{ borderRadius: 16, overflow: "hidden", border: `1px solid ${LINE}` }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead style={{ background: MINT }}>
                <tr>
                  {(() => {
                    const hasNotes = packages.some(p => p.notes && p.notes.trim() !== "");
                    const headers = ["Package", "Rates", "Total"];
                    if (hasNotes) headers.push("Notes");
                    return headers.map((h, i) => (
                      <th key={i} style={{ padding: "12px 14px", textAlign: "left", color: TEAL, fontSize: 11, letterSpacing: 1, fontWeight: 700 }}>{h}</th>
                    ));
                  })()}
                </tr>
              </thead>
              <tbody>
                {packages.map((p, i) => {
                  const hasNotes = packages.some(pkg => pkg.notes && pkg.notes.trim() !== "");
                  return (
                    <tr key={i} style={{ borderTop: `1px solid ${LINE}` }}>
                      <td style={{ padding: "12px 14px", fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>{p.name}</td>
                      <td style={{ padding: "12px 14px", color: TEAL_DARK, fontWeight: 600, fontSize: 11 }}>
                        {p.price_per_person ? `₹${Number(p.price_per_person).toLocaleString("en-IN")}/person` : 
                          [
                            p.price_per_adult ? `Adult: ₹${Number(p.price_per_adult).toLocaleString("en-IN")}` : null,
                            p.price_per_child ? `Child: ₹${Number(p.price_per_child).toLocaleString("en-IN")}` : null,
                            p.price_per_infant ? `Infant: ₹${Number(p.price_per_infant).toLocaleString("en-IN")}` : null
                          ].filter(Boolean).join(" | ") || "—"}
                      </td>
                      <td style={{ padding: "12px 14px", color: TEAL_DARK, fontWeight: 700 }}>{p.price_total ? `₹${Number(p.price_total).toLocaleString("en-IN")}` : "—"}</td>
                      {hasNotes && <td style={{ padding: "12px 14px", color: MUTED }}>{p.notes}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section style={{ padding: "12px 28px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div style={{ background: "white", border: `1px solid ${LINE}`, borderRadius: 16, padding: "18px 20px", position: "relative" }}>
          <div style={{ position: "absolute", top: -10, left: 18, background: TEAL, color: "white", padding: "4px 14px", borderRadius: 999, fontSize: 10, letterSpacing: 1.5, fontWeight: 700 }}>INCLUSIONS</div>
          <ul style={{ margin: "8px 0 0 0", padding: 0, listStyle: "none" }}>
            {inclusions.map((x, i) => (
              <li key={i} style={{ fontSize: 12, padding: "4px 0 4px 18px", position: "relative", fontFamily: "'EB Garamond', serif", color: "#374151" }}>
                <span style={{ position: "absolute", left: 0, color: TEAL, fontWeight: 700 }}>✓</span>
                {x}
              </li>
            ))}
          </ul>
        </div>
        <div style={{ background: "white", border: `1px solid ${LINE}`, borderRadius: 16, padding: "18px 20px", position: "relative" }}>
          <div style={{ position: "absolute", top: -10, left: 18, background: "#9F1239", color: "white", padding: "4px 14px", borderRadius: 999, fontSize: 10, letterSpacing: 1.5, fontWeight: 700 }}>EXCLUSIONS</div>
          <ul style={{ margin: "8px 0 0 0", padding: 0, listStyle: "none" }}>
            {exclusions.map((x, i) => (
              <li key={i} style={{ fontSize: 12, padding: "4px 0 4px 18px", position: "relative", fontFamily: "'EB Garamond', serif", color: "#374151" }}>
                <span style={{ position: "absolute", left: 0, color: "#9F1239", fontWeight: 700 }}>✗</span>
                {x}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {tc && (
        <section style={{ padding: "16px 28px 12px" }}>
          <div style={{ background: "white", border: `1px solid ${LINE}`, borderRadius: 16, padding: "18px 22px" }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", color: TEAL, fontSize: 18, fontWeight: 700, margin: "0 0 10px" }}>Terms &amp; Conditions</h2>
            <pre style={{ whiteSpace: "pre-wrap", fontSize: 11, lineHeight: 1.7, color: "#374151", fontFamily: "'EB Garamond', serif", margin: 0 }}>{tc}</pre>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer style={{ padding: "20px 28px 28px", textAlign: "center", borderTop: `1px solid ${LINE}`, marginTop: 8 }}>
        <div style={{ fontFamily: "'Playfair Display', serif", color: TEAL, fontWeight: 700, fontSize: 18 }}>Happy Journey 4 U</div>
        <div style={{ color: MUTED, fontSize: 11, marginTop: 2, fontStyle: "italic", fontFamily: "'EB Garamond', serif" }}>by Lucky Jeswani · Aapka safar, humari zimmedaari</div>
        <div style={{ marginTop: 8, display: "flex", gap: 14, justifyContent: "center", color: MUTED, fontSize: 10, letterSpacing: 1 }}>
          <span>+91 97532 77771, +91 79871 52260</span>
          <span>·</span>
          <span>support@happyjourney4u.co.in</span>
        </div>
      </footer>
    </div>
  );
}
